import React from 'react';
import {BuyingOption, DeliveryPromiseTime, PriceType, VisitType} from 'state/types';
import DeliveryPromiseUtils from 'state/utils/DeliveryPromiseUtils';
import {CLINIC_CDP_CURRENT_ASIN_DECOUPLE_TWISTER_OFFER_INFO, isTreatmentT1} from "../../../utils";

export interface TwisterVariationProps {
    visitType: VisitType;
    title: string;
    priceLabel: string;
    /**
     * All qualified buying options for selected state of the twister variation.
     */
    qualifiedBuyingOptions: BuyingOption[]
    /**
     * Merchant delivery promises info for the twister variation. It contains merchantId, response time info and business hours info.
     */
    merchantDeliveryPromises: DeliveryPromiseTime[]
    deliveryPromiseTimes: DeliveryPromiseTime[]
    isSelected: boolean;
    onClick?: React.MouseEventHandler<HTMLButtonElement>
}

const renderVariationPrice = (price: PriceType) => {
    return `${price.currencySymbol ?? ""}${price.amount.toFixed(2)}`;
}

const TwisterVariation = (props: TwisterVariationProps) => {
    const {visitType, qualifiedBuyingOptions, merchantDeliveryPromises} = props;
    const visitTypeLowerCase = visitType.toLowerCase();
    const iconUrl = visitTypeLowerCase === "synchronous"
        ? `https://m.media-amazon.com/images/G/01/katara/icons/sync-final/Video-blue.svg`
        : 'https://m.media-amazon.com/images/G/01/katara/icons/sync-final/Chat-blue.svg'

    const lowestPriceOfOpenClinics = [...qualifiedBuyingOptions]
        .filter(bo => DeliveryPromiseUtils.checkIfClinicOpen(bo.obfuscatedMerchantId, merchantDeliveryPromises))
        .sort((bo1, bo2) => bo1.priceToPay.amount - bo2.priceToPay.amount)
        .shift()?.priceToPay.amount
    const lowestPriceBOsOfOpenClinics = [...qualifiedBuyingOptions]
        .filter(bo => DeliveryPromiseUtils.checkIfClinicOpen(bo.obfuscatedMerchantId, merchantDeliveryPromises))
        .filter(bo => bo.priceToPay.amount === lowestPriceOfOpenClinics)

    const generateVariationPriceMessageElement = () => {
        const priceDisplayString = lowestPriceBOsOfOpenClinics.length !== 0 ? renderVariationPrice(lowestPriceBOsOfOpenClinics[0].priceToPay) : undefined
        if (!!props.priceLabel && !!priceDisplayString) {
            return <pui-section flowdirection="horizontal">
                <div>
                    <span>{props.priceLabel}</span>&#32;<strong>{priceDisplayString}</strong>
                </div>
            </pui-section>
        }
    }

    const generateDeliveryPromiseMessageElement = () => {

        if (props.deliveryPromiseTimes.length === 0) {
            // No delivery promise found, do not show delivery promise widget.
            return <></>
        }

        if (lowestPriceBOsOfOpenClinics.length === 0) {
            // If no lowest price buyingoption found, this means all clinic are closed, will show next available clinic business_hour_start_time
            const soonestOpenedClinicPromise = [...props.deliveryPromiseTimes].sort((t1, t2) => {
                return (t2.operatingStatus?.toggleInMinute ?? 0) - (t1.operatingStatus?.toggleInMinute ?? 0)
            }).pop()
            if (soonestOpenedClinicPromise?.businessHours?.endOffsetTime) {
                const startTimeText = DeliveryPromiseUtils.buildBusinessHourLocalTime(soonestOpenedClinicPromise?.businessHours?.startOffsetTime, true)
                return <><span className="clinic-closed">{`Unavailable until ${startTimeText}`}</span></>
            } else {
                // ideally this won't happen
                return <></>
            }
        } else {
            let deliveryPromiseLabel;
            if ("synchronous" === visitTypeLowerCase) {
                deliveryPromiseLabel = 'Wait time from'
            } else {
                deliveryPromiseLabel = 'Response time from'
            }
            let fastestResponseTimes;
            if (!isTreatmentT1(CLINIC_CDP_CURRENT_ASIN_DECOUPLE_TWISTER_OFFER_INFO)) {
                //Getting the fastest response times from current open clinics with the lowest price
                fastestResponseTimes = lowestPriceBOsOfOpenClinics
                    .map(bo => props.deliveryPromiseTimes.find(dpt => dpt.obfuscatedMerchantId === bo.obfuscatedMerchantId))
                    .map(dpt => dpt?.responseTime?.latestResponseInSecond)
                    .filter(sec => sec !== undefined) as number[]
            } else {
                //Getting the fastest response times from all current open clinics.
                fastestResponseTimes = props.deliveryPromiseTimes
                    .filter(dpt => dpt.operatingStatus?.isClinicOpen ?? true)
                    .map(dpt => dpt?.responseTime?.latestResponseInSecond)
                    .filter(sec => sec !== undefined) as number[]
            }

            if (fastestResponseTimes.length !== 0) {
                return (<span style={{display: 'flex', flexDirection: 'column'}}>
                    <span>{deliveryPromiseLabel}</span>
                    <strong>{DeliveryPromiseUtils.buildVariationResponseTimeText(Math.min(...fastestResponseTimes))}</strong>
                </span>)
            } else {
                // No deliery promise can be found from all lowest price providers. Ideally this won't happen, but in case dirty data, show nothing.
                return <></>
            }
        }
    }

    return (
        <button className={`variation-card ${props.isSelected ? 'selected' : ''}`} onClick={props.onClick}>
            <pui-card fullWidth noShadow>
                <pui-section flowdirection="horizontal" padding="mini" paddingTop="small" paddingBottom="small" class="heading">
                    <img src={iconUrl} className='title-icon pui-inline-block'/>
                    <pui-heading input={props.title} inline spacingLeft="mini" textSize="small" class="text-word-break"/>
                </pui-section>
                <pui-divider/>
                <pui-section padding="small">
                    {generateVariationPriceMessageElement()}
                    {generateDeliveryPromiseMessageElement()}
                </pui-section>
            </pui-card>
        </button>
    )
}
export default TwisterVariation;